import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { apiClient } from '../services/apiClient';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [monthlyRequests, setMonthlyRequests] = useState(null);
  const [feedbackCount, setFeedbackCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract custom claims from the user object
  const customClaims = user ? {
    group: user['https://qualitee.ai/group'],
    clinic: user['https://qualitee.ai/clinic'],
    flics: user['https://qualitee.ai/flics'],
    abo: user['https://qualitee.ai/abo']
  } : null;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [userStats, requestStats, feedbackStats] = await Promise.all([
          apiClient.get('/api/admin/users/stats'),
          apiClient.get('/api/admin/statistics/current-month-count'),
          apiClient.get('/api/admin/feedback/count-last-30-days')
        ]);
        setStats(userStats);
        setMonthlyRequests(requestStats);
        setFeedbackCount(feedbackStats);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleTileClick = (path) => {
    navigate(path);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord administrateur</h1>
        <p className="mt-2 text-gray-600">
          Bienvenue, {user ? user.name : 'Admin'}. Surveillez et gérez votre plateforme de santé QualiteeMed.ai.
        </p>
      </div>

      {/* Cartes de statistiques */}
      <Grid container spacing={3}>
        {/* Carte des utilisateurs totaux */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleTileClick('/admin-users')}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                  Utilisateurs totaux
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
                ) : error ? (
                  <Typography variant="h4">-</Typography>
                ) : (
                  <Typography variant="h4">{stats?.totalUsers || 0}</Typography>
                )}
              </Box>
              <Icons.People sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Carte des administrateurs */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleTileClick('/admin-users')}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'secondary.light',
              color: 'secondary.contrastText',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                  Administrateurs
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'secondary.contrastText' }} />
                ) : error ? (
                  <Typography variant="h4">-</Typography>
                ) : (
                  <Typography variant="h4">{stats?.adminUsers || 0}</Typography>
                )}
              </Box>
              <Icons.AdminPanelSettings sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Carte des requêtes mensuelles */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleTileClick('/statistics')}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'success.light',
              color: 'success.contrastText',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                  Requêtes ce mois
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'success.contrastText' }} />
                ) : error ? (
                  <Typography variant="h4">-</Typography>
                ) : (
                  <Typography variant="h4">{monthlyRequests?.count || 0}</Typography>
                )}
              </Box>
              <Icons.Description sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Carte des feedbacks des 30 derniers jours */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleTileClick('/feedback')}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                  Feedbacks (30j)
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'warning.contrastText' }} />
                ) : error ? (
                  <Typography variant="h4">-</Typography>
                ) : (
                  <Typography variant="h4">{feedbackCount?.count || 0}</Typography>
                )}
              </Box>
              <Icons.Feedback sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard; 